import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import { getWorkCenters, createWorkCenter, updateWorkCenter, deleteWorkCenter, getWorkCenterSchedule, updateWorkCenterSchedule } from '../services/api';
import { WorkCenter, WorkSchedule } from '../types';

const timezones = [
  'UTC',
  'Europe/Madrid',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles'
];

const WorkCenters: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [workCenters, setWorkCenters] = useState<WorkCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingWorkCenter, setEditingWorkCenter] = useState<WorkCenter | null>(null);
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [openScheduleConfigDialog, setOpenScheduleConfigDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<WorkSchedule[]>([]);
  const [selectedWorkCenter, setSelectedWorkCenter] = useState<WorkCenter | null>(null);
  const [newWorkCenterId, setNewWorkCenterId] = useState<number | null>(null);
  const [scheduleConfig, setScheduleConfig] = useState([
    { day_of_week: 0, day_name: 'Lunes', work_hours: 8, is_working_day: true },
    { day_of_week: 1, day_name: 'Martes', work_hours: 8, is_working_day: true },
    { day_of_week: 2, day_name: 'Miércoles', work_hours: 8, is_working_day: true },
    { day_of_week: 3, day_name: 'Jueves', work_hours: 8, is_working_day: true },
    { day_of_week: 4, day_name: 'Viernes', work_hours: 8, is_working_day: true },
    { day_of_week: 5, day_name: 'Sábado', work_hours: 0, is_working_day: false },
    { day_of_week: 6, day_name: 'Domingo', work_hours: 0, is_working_day: false },
  ]);
  const [newWorkCenter, setNewWorkCenter] = useState({
    name: '',
    location: '',
    timezone: 'Europe/Madrid',
    daily_work_hours: '8',
    description: ''
  });

  const handleEdit = (workCenter: WorkCenter) => {
    setEditingWorkCenter(workCenter);
    setNewWorkCenter({
      name: workCenter.name,
      location: workCenter.location,
      timezone: workCenter.timezone,
      daily_work_hours: workCenter.daily_work_hours.toString(),
      description: workCenter.description || ''
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar este centro de trabajo?')) {
      try {
        await deleteWorkCenter(id);
        const updatedCenters = await getWorkCenters();
        setWorkCenters(updatedCenters);
      } catch (error) {
        console.error('Error deleting work center:', error);
        alert('Error al eliminar el centro de trabajo');
      }
    }
  };

  const handleViewSchedule = async (workCenter: WorkCenter) => {
    try {
      const schedule = await getWorkCenterSchedule(workCenter.id);
      setSelectedSchedule(schedule);
      setSelectedWorkCenter(workCenter);
      setEditingSchedule(false);
      
      // Cargar el horario actual en scheduleConfig para poder editarlo
      const dayNames = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
      const loadedSchedule = dayNames.map((dayName, index) => {
        const existingSchedule = schedule.find(s => s.day_of_week === index);
        return {
          day_of_week: index,
          day_name: dayName,
          work_hours: existingSchedule?.work_hours || 0,
          is_working_day: existingSchedule?.is_working_day || false
        };
      });
      setScheduleConfig(loadedSchedule);
      
      setOpenScheduleDialog(true);
    } catch (error) {
      console.error('Error loading schedule:', error);
      alert('Error al cargar el horario');
    }
  };

  const getDayName = (dayOfWeek: number) => {
    const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    return days[dayOfWeek];
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Nombre', width: 200 },
    { field: 'location', headerName: 'Ubicación', width: 200 },
    { field: 'timezone', headerName: 'Zona Horaria', width: 150 },
    { field: 'daily_work_hours', headerName: 'Horas/Día', width: 120 },
    { field: 'description', headerName: 'Descripción', width: 250 },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ScheduleIcon />}
          label="Ver Horario"
          onClick={() => handleViewSchedule(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Editar"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Eliminar"
          onClick={() => handleDelete(params.row.id)}
        />
      ]
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getWorkCenters();
        setWorkCenters(data);
      } catch (error) {
        console.error('Error fetching work centers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCreateOrUpdateWorkCenter = async () => {
    try {
      const workCenterData = {
        ...newWorkCenter,
        daily_work_hours: Number(newWorkCenter.daily_work_hours)
      };

      if (editingWorkCenter) {
        const updatedWorkCenter = await updateWorkCenter(editingWorkCenter.id, workCenterData);
        setWorkCenters(workCenters.map(wc => wc.id === editingWorkCenter.id ? updatedWorkCenter : wc));
        handleCloseDialog();
      } else {
        const createdWorkCenter = await createWorkCenter(workCenterData);
        setWorkCenters([...workCenters, createdWorkCenter]);
        setNewWorkCenterId(createdWorkCenter.id);
        handleCloseDialog();
        // Abrir diálogo de configuración de horario
        setOpenScheduleConfigDialog(true);
      }
    } catch (error) {
      console.error('Error saving work center:', error);
      alert('Error al guardar el centro de trabajo');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingWorkCenter(null);
    setNewWorkCenter({
      name: '',
      location: '',
      timezone: 'Europe/Madrid',
      daily_work_hours: '8',
      description: ''
    });
  };

  const handleSaveScheduleConfig = async () => {
    if (!newWorkCenterId) return;
    
    try {
      const schedules = scheduleConfig.map(day => ({
        day_of_week: day.day_of_week,
        work_hours: day.work_hours,
        is_working_day: day.is_working_day
      }));
      
      await updateWorkCenterSchedule(newWorkCenterId, schedules);
      setOpenScheduleConfigDialog(false);
      setNewWorkCenterId(null);
      // Reiniciar configuración de horario a valores por defecto
      setScheduleConfig([
        { day_of_week: 0, day_name: 'Lunes', work_hours: 8, is_working_day: true },
        { day_of_week: 1, day_name: 'Martes', work_hours: 8, is_working_day: true },
        { day_of_week: 2, day_name: 'Miércoles', work_hours: 8, is_working_day: true },
        { day_of_week: 3, day_name: 'Jueves', work_hours: 8, is_working_day: true },
        { day_of_week: 4, day_name: 'Viernes', work_hours: 8, is_working_day: true },
        { day_of_week: 5, day_name: 'Sábado', work_hours: 0, is_working_day: false },
        { day_of_week: 6, day_name: 'Domingo', work_hours: 0, is_working_day: false },
      ]);
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Error al guardar el horario');
    }
  };

  const handleScheduleConfigChange = (index: number, field: string, value: any) => {
    const updatedSchedule = [...scheduleConfig];
    updatedSchedule[index] = { ...updatedSchedule[index], [field]: value };
    setScheduleConfig(updatedSchedule);
  };

  const handleSaveEditedSchedule = async () => {
    if (!selectedWorkCenter) return;
    
    try {
      const schedules = scheduleConfig.map(day => ({
        day_of_week: day.day_of_week,
        work_hours: day.work_hours,
        is_working_day: day.is_working_day
      }));
      
      await updateWorkCenterSchedule(selectedWorkCenter.id, schedules);
      
      // Recargar el horario actualizado
      const updatedSchedule = await getWorkCenterSchedule(selectedWorkCenter.id);
      setSelectedSchedule(updatedSchedule);
      setEditingSchedule(false);
    } catch (error) {
      console.error('Error updating schedule:', error);
      alert('Error al actualizar el horario');
    }
  };

  const handleCancelEditSchedule = async () => {
    if (!selectedWorkCenter) return;
    
    // Recargar el horario original
    const schedule = await getWorkCenterSchedule(selectedWorkCenter.id);
    const dayNames = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const loadedSchedule = dayNames.map((dayName, index) => {
      const existingSchedule = schedule.find(s => s.day_of_week === index);
      return {
        day_of_week: index,
        day_name: dayName,
        work_hours: existingSchedule?.work_hours || 0,
        is_working_day: existingSchedule?.is_working_day || false
      };
    });
    setScheduleConfig(loadedSchedule);
    setEditingSchedule(false);
  };

  return (
    <Box sx={{ p: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'start' }} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }} mb={{ xs: 3, sm: 6 }}>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 1,
              fontWeight: 700,
              color: '#001923',
              letterSpacing: '-0.01em',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Centros de Trabajo
          </Typography>
          <Typography 
            variant="body1" 
            className="descripcion-cyan"
            sx={{ 
              fontWeight: 300,
              lineHeight: 1.6
            }}
          >
            Administración de ubicaciones laborales
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{
            backgroundColor: '#004254',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#003040'
            },
            alignSelf: { xs: 'stretch', sm: 'auto' }
          }}
        >
          Nuevo Centro
        </Button>
      </Box>

      <Paper 
        className="workcenters-datagrid-paper"
        sx={{ 
          height: { xs: 'calc(100vh - 280px)', sm: 600 },
          minHeight: 400,
          width: '100%',
          backgroundColor: '#FFFFFF',
          '&.workcenters-datagrid-paper .MuiDataGrid-root': {
            border: 'none',
            backgroundColor: '#FFFFFF'
          },
          '&.workcenters-datagrid-paper .MuiDataGrid-columnHeaders': {
            backgroundColor: '#00B0BD',
            borderBottom: '2px solid #00B0BD',
            color: '#001923'
          },
          '&.workcenters-datagrid-paper .MuiDataGrid-columnHeaderTitle': {
            color: '#001923',
            fontWeight: 600
          },
          '&.workcenters-datagrid-paper .MuiDataGrid-columnHeader': {
            padding: { xs: '0 8px', sm: '0 16px' }
          },
          '&.workcenters-datagrid-paper .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
            display: { xs: 'none !important', sm: 'inline-flex' }
          },
          '&.workcenters-datagrid-paper .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
            display: { xs: 'none !important', sm: 'inline-flex' }
          },
          '&.workcenters-datagrid-paper .MuiDataGrid-cell': {
            borderBottom: '1px solid #f0f0f0',
            color: '#001923'
          },
          '@media (max-width: 600px)': {
            '&.workcenters-datagrid-paper .MuiDataGrid-actionsCell': {
              gap: '0px'
            },
            '&.workcenters-datagrid-paper .MuiDataGrid-actionsCell .MuiButtonBase-root': {
              padding: '4px',
              minWidth: 'unset'
            }
          },
          '&.workcenters-datagrid-paper .MuiDataGrid-row': {
            backgroundColor: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }
        }}>
        <DataGrid
          rows={workCenters}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 }
            }
          }}
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnMenu={isMobile}
        />
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingWorkCenter ? 'Editar Centro de Trabajo' : 'Crear Nuevo Centro de Trabajo'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Nombre"
              value={newWorkCenter.name}
              onChange={(e) => setNewWorkCenter({ ...newWorkCenter, name: e.target.value })}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Ubicación"
              value={newWorkCenter.location}
              onChange={(e) => setNewWorkCenter({ ...newWorkCenter, location: e.target.value })}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Zona Horaria</InputLabel>
              <Select
                value={newWorkCenter.timezone}
                label="Zona Horaria"
                onChange={(e) => setNewWorkCenter({ ...newWorkCenter, timezone: e.target.value })}
              >
                {timezones.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              label="Horas de Trabajo Diarias"
              type="number"
              inputProps={{ step: 0.5, min: 1, max: 24 }}
              value={newWorkCenter.daily_work_hours}
              onChange={(e) => setNewWorkCenter({ ...newWorkCenter, daily_work_hours: e.target.value })}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Descripción (opcional)"
              multiline
              rows={3}
              value={newWorkCenter.description}
              onChange={(e) => setNewWorkCenter({ ...newWorkCenter, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              backgroundColor: '#FFFFFF',
              color: '#004254',
              borderColor: '#004254',
              textTransform: 'none',
              fontFamily: 'ForFutureSans, sans-serif',
              fontWeight: 700,
              borderRadius: 0,
              px: 3,
              '&:hover': { backgroundColor: '#f0f7f9', borderColor: '#004254' },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateOrUpdateWorkCenter}
            variant="contained"
            sx={{
              backgroundColor: '#004254',
              color: '#FFFFFF',
              textTransform: 'none',
              fontFamily: 'ForFutureSans, sans-serif',
              fontWeight: 700,
              borderRadius: 0,
              px: 3,
              '&:hover': { backgroundColor: '#003040' },
            }}
          >
            {editingWorkCenter ? 'Actualizar' : 'Crear Centro'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={openScheduleDialog} onClose={() => setOpenScheduleDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Horario de {selectedWorkCenter?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {selectedWorkCenter?.description}
            </Typography>
            
            {!editingSchedule ? (
              /* Modo Visualización */
              selectedSchedule.length > 0 ? (
                <Box>
                  {selectedSchedule
                    .filter(s => s.is_working_day)
                    .sort((a, b) => a.day_of_week - b.day_of_week)
                    .map((schedule) => (
                      <Box key={schedule.id} sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        py: 1, 
                        borderBottom: '1px solid #eee' 
                      }}>
                        <Typography>{getDayName(schedule.day_of_week)}</Typography>
                        <Typography sx={{ fontWeight: 'bold' }}>
                          {schedule.work_hours}h
                        </Typography>
                      </Box>
                    ))}
                  
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle2">
                      Horas semanales totales: {' '}
                      <strong>
                        {selectedSchedule
                          .filter(s => s.is_working_day)
                          .reduce((total, s) => total + s.work_hours, 0)}h
                      </strong>
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography>No hay horario definido para este centro</Typography>
              )
            ) : (
              /* Modo Edición */
              <Box>
                <Grid container spacing={2}>
                  {scheduleConfig.map((day, index) => (
                    <Grid item xs={12} key={day.day_of_week}>
                      <Paper sx={{ p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={day.is_working_day}
                                  onChange={(e) => handleScheduleConfigChange(index, 'is_working_day', e.target.checked)}
                                />
                              }
                              label={<strong>{day.day_name}</strong>}
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <TextField
                              fullWidth
                              label="Horas de trabajo"
                              type="number"
                              value={day.work_hours}
                              onChange={(e) => handleScheduleConfigChange(index, 'work_hours', parseFloat(e.target.value) || 0)}
                              disabled={!day.is_working_day}
                              inputProps={{ min: 0, max: 24, step: 0.5 }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle1">
                    Total horas semanales: {' '}
                    <strong>
                      {scheduleConfig
                        .filter(d => d.is_working_day)
                        .reduce((total, d) => total + d.work_hours, 0)}h
                    </strong>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {!editingSchedule ? (
            <>
              <Button onClick={() => setOpenScheduleDialog(false)}>Cerrar</Button>
              <Button 
                onClick={() => setEditingSchedule(true)} 
                variant="contained"
                startIcon={<EditIcon />}
              >
                Editar Horario
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCancelEditSchedule}>Cancelar</Button>
              <Button onClick={handleSaveEditedSchedule} variant="contained">
                Guardar Cambios
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Diálogo de Configuración de Horario para Nuevo Centro */}
      <Dialog 
        open={openScheduleConfigDialog} 
        onClose={() => setOpenScheduleConfigDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Configurar Horario del Centro de Trabajo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Configure las horas de trabajo para cada día de la semana
          </Typography>
          <Grid container spacing={2}>
            {scheduleConfig.map((day, index) => (
              <Grid item xs={12} key={day.day_of_week}>
                <Paper sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={day.is_working_day}
                            onChange={(e) => handleScheduleConfigChange(index, 'is_working_day', e.target.checked)}
                          />
                        }
                        label={<strong>{day.day_name}</strong>}
                      />
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <TextField
                        fullWidth
                        label="Horas de trabajo"
                        type="number"
                        value={day.work_hours}
                        onChange={(e) => handleScheduleConfigChange(index, 'work_hours', parseFloat(e.target.value) || 0)}
                        disabled={!day.is_working_day}
                        inputProps={{ min: 0, max: 24, step: 0.5 }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle1">
              Total horas semanales: {' '}
              <strong>
                {scheduleConfig
                  .filter(d => d.is_working_day)
                  .reduce((total, d) => total + d.work_hours, 0)}h
              </strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenScheduleConfigDialog(false);
            setNewWorkCenterId(null);
          }}>
            Cancelar
          </Button>
          <Button onClick={handleSaveScheduleConfig} variant="contained">
            Guardar Horario
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkCenters;